import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/**
 * Context for managing batch operations (adding/removing multiple anime)
 * Provides:
 * - Progress tracking that persists across tab switches
 * - Cancel mechanism via AbortController
 * - Global state accessible from any component
 */

const BatchOperationContext = createContext(null);

export const BatchOperationProvider = ({ children }) => {
    // Operation state
    const [isOperationActive, setIsOperationActive] = useState(false);
    const [operationType, setOperationType] = useState(null); // 'adding' | 'updating' | null
    const [totalItems, setTotalItems] = useState(0);
    const [completedItems, setCompletedItems] = useState(0);
    const [failedItems, setFailedItems] = useState(0);
    const [currentItem, setCurrentItem] = useState(null); // { id, title, attempt }
    const [isCancelled, setIsCancelled] = useState(false);

    // AbortController for cancellation
    const abortControllerRef = useRef(null);

    /**
     * Start a new batch operation
     * @param {string} type - 'adding' | 'updating'
     * @param {number} total - Total number of items
     */
    const startOperation = useCallback((type, total) => {
        // Create new AbortController
        abortControllerRef.current = new AbortController();

        setIsOperationActive(true);
        setOperationType(type);
        setTotalItems(total);
        setCompletedItems(0);
        setFailedItems(0);
        setCurrentItem(null);
        setIsCancelled(false);

        console.log(`🚀 Started batch operation: ${type} (${total} items)`);
    }, []);

    /**
     * Update progress for current item
     * @param {object} item - { id, title, attempt }
     */
    const updateCurrentItem = useCallback((item) => {
        setCurrentItem(item);
    }, []);

    /**
     * Mark an item as completed
     */
    const markItemCompleted = useCallback(() => {
        setCompletedItems(prev => prev + 1);
        console.log(`✅ Item completed`);
    }, []);

    /**
     * Mark an item as failed
     */
    const markItemFailed = useCallback(() => {
        setFailedItems(prev => prev + 1);
        console.log(`❌ Item failed`);
    }, []);

    /**
     * Cancel the ongoing operation
     */
    const cancelOperation = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setIsCancelled(true);
            console.log(`🛑 Operation cancelled by user`);
        }
    }, []);

    /**
     * End the operation (success or cancelled)
     */
    const endOperation = useCallback(() => {
        setIsOperationActive(false);
        setOperationType(null);
        abortControllerRef.current = null;
        console.log(`🏁 Operation ended: ${completedItems} completed, ${failedItems} failed`);
    }, [completedItems, failedItems]);

    /**
     * Check if operation is cancelled
     */
    const checkCancelled = useCallback(() => {
        return isCancelled || abortControllerRef.current?.signal.aborted;
    }, [isCancelled]);

    /**
     * Get progress percentage
     */
    const getProgress = useCallback(() => {
        if (totalItems === 0) return 0;
        return Math.round((completedItems / totalItems) * 100);
    }, [completedItems, totalItems]);

    /**
     * Get abort signal for passing to async operations
     */
    const getAbortSignal = useCallback(() => {
        return abortControllerRef.current?.signal;
    }, []);

    const value = {
        // State
        isOperationActive,
        operationType,
        totalItems,
        completedItems,
        failedItems,
        currentItem,
        isCancelled,

        // Methods
        startOperation,
        updateCurrentItem,
        markItemCompleted,
        markItemFailed,
        cancelOperation,
        endOperation,
        checkCancelled,
        getProgress,
        getAbortSignal,
    };

    return (
        <BatchOperationContext.Provider value={value}>
            {children}
        </BatchOperationContext.Provider>
    );
};

/**
 * Hook to use batch operation context
 */
export const useBatchOperation = () => {
    const context = useContext(BatchOperationContext);
    if (!context) {
        throw new Error('useBatchOperation must be used within BatchOperationProvider');
    }
    return context;
};

export default BatchOperationContext;
